// Starter Tape curriculum — the tapes of Side A: First Sounds. Each tape is
// one short track with a brief (mood, palette, one "must", optional tries),
// an idea card that names the concept, and the studio setup it opens with.
//
// Later tapes can start from an earlier tape's saved take (`setup.fromTape`)
// so you hear your own music change when one thing changes. The must checks and listen-back notes live in starterTapeListen.js,
// keyed by `must.id` and the tape `id`.

import {
  HAT,
  INSTRUMENTS,
  KICK,
  arpeggiate,
  createSong,
  diatonicChord,
  loopSeconds,
  makeSections,
  setBarChord,
} from "./starterTape";

// A minor pentatonic notes in the pad's range (A3–A4), the bell's (A4–A5)
// and the bass's (A1–A2).
const PAD_PENTATONIC = [57, 60, 62, 64, 67, 69];
const BELL_PENTATONIC = [69, 72, 74, 76, 79, 81];
const BASS_PENTATONIC = [33, 36, 38, 40, 43, 45];

// A held A-minor pad chord across `steps`, used as a backdrop.
const heldAm = (steps) =>
  [57, 60, 64].map((pitch) => ({ pitch, step: 0, length: steps }));

// Kick on every beat 1, hats on every off-beat ("and"), for `bars` bars of
// eight steps.
const softBeat = (bars) => {
  const notes = [];
  for (let bar = 0; bar < bars; bar += 1) {
    const s = bar * 8;
    notes.push({ pitch: KICK, step: s, length: 1 });
    notes.push({ pitch: KICK, step: s + 4, length: 1 });
    [1, 3, 5, 7].forEach((o) =>
      notes.push({ pitch: HAT, step: s + o, length: 1 })
    );
  }
  return notes;
};

// The bell phrase from the sound check, for when there's no Home take yet.
const HOME_FALLBACK_BELL = [
  { pitch: 76, step: 0, length: 2 },
  { pitch: 74, step: 2, length: 2 },
  { pitch: 72, step: 4, length: 4 },
  { pitch: 76, step: 10, length: 2 },
  { pitch: 79, step: 16, length: 2 },
  { pitch: 76, step: 18, length: 2 },
  { pitch: 74, step: 20, length: 4 },
  { pitch: 69, step: 26, length: 6 },
];

// The Home bell phrase repeated to fill `bars` bars (it's 4 bars long).
const homeMelody = (bars) => {
  const notes = [];
  for (let offset = 0; offset < bars; offset += 4) {
    HOME_FALLBACK_BELL.forEach((n) =>
      notes.push({ ...n, step: n.step + offset * 8 })
    );
  }
  return notes;
};

export const SIDE_A = [
  {
    id: "hold",
    number: 1,
    title: "Hold",
    mood: "A single sound that swells in, hangs in the air, then fades away.",
    palette: "Pad · one note (A)",
    must: { id: "holdLong", text: "Hold a note for at least 1 bar." },
    tries: [
      "Play it with the slow swell, then the fast one. Listen to the first second.",
      "Make the note short, then long. Which one feels like it breathes?",
    ],
    idea: {
      title: "Every sound has a shape",
      body: "A note doesn't just switch on and off. It swells in (the attack), holds, then fades out (the release). That shape is called its envelope, and it's a big part of why a pad feels soft and a bell feels sharp.",
    },
    setup: {
      bpm: 72,
      bars: 2,
      tracks: [{ id: "pad", instrument: "pad", pitches: [57], attack: 1.2 }],
    },
    controls: ["swell"],
    unlocks: ["A second note"],
  },
  {
    id: "pairs",
    number: 2,
    title: "Pairs",
    mood: "Two notes leaning on each other.",
    palette: "Pad · A, C, D, E and the A above",
    must: {
      id: "twoTogether",
      text: "Make two different notes sound at the same time.",
    },
    tries: [
      "Try A with E, then A with D. Which pair blends and which one leans?",
      "Try A with the A above it. Does it sound like one note or two?",
    ],
    idea: {
      title: "Intervals: the distance between notes",
      body: "Two notes played together make an interval. Some pairs blend so well they almost sound like one note, like the octave (A with the A above) and the fifth (A with E). Others rub a little, which adds color and a bit of tension.",
    },
    setup: {
      bpm: 72,
      bars: 3,
      tracks: [
        {
          id: "pad",
          instrument: "pad",
          pitches: [57, 60, 62, 64, 69],
          attack: 1.2,
        },
      ],
    },
    controls: [],
    unlocks: ["The bell", "All five notes of the scale", "Longer loops"],
  },
  {
    id: "home",
    number: 3,
    title: "Home",
    mood: "Something calm that loops gently and comes to rest.",
    palette: "Pad + bell · A minor pentatonic",
    must: {
      id: "endOnRoot",
      text: "The bell melody ends on A (1), the home note.",
    },
    tries: [
      "Use the same three notes twice, in a different order.",
      "Leave one whole bar with no bell notes.",
    ],
    idea: {
      title: "The root is home",
      body: "Every key has a home note called the root. Here it's A, numbered 1. A melody that ends on the root sounds finished; ending anywhere else sounds like a question. The five notes you can use make the A minor pentatonic scale, a palette where any two notes sound good together.",
    },
    setup: {
      bpm: 72,
      bars: 4,
      tracks: [
        { id: "pad", instrument: "pad", pitches: PAD_PENTATONIC, attack: 1.2 },
        { id: "bell", instrument: "bell", pitches: BELL_PENTATONIC },
      ],
    },
    controls: [],
    unlocks: ["The sandbox studio", "Drums"],
  },
  {
    id: "pulse",
    number: 4,
    title: "Pulse",
    mood: "A soft, steady heartbeat under a held chord.",
    palette: "Drums (kick, snare, hat) · tempo",
    must: {
      id: "kickOnOne",
      text: "Put a kick on the first beat of every bar.",
    },
    tries: [
      "Put hats on the “ands”, the squares between the beats.",
      "Add a snare on beats 2 and 4, then take it away. Which feels calmer?",
      "Drag the tempo slider. Does the same pattern feel like a different song?",
    ],
    idea: {
      title: "Beat and tempo",
      body: "Most music sits on a steady pulse called the beat. Here every bar has 4 beats, and each beat is split in two: the beat itself and the “and” after it (1 and 2 and 3 and 4 and). Tempo is how fast the beats go, counted in beats per minute (bpm). A kick on beat 1 tells your ear where each bar starts.",
    },
    setup: {
      bpm: 72,
      bars: 4,
      tracks: [
        {
          id: "pad",
          instrument: "pad",
          pitches: PAD_PENTATONIC,
          notes: heldAm(32),
        },
        { id: "drums", instrument: "drums" },
      ],
    },
    controls: ["tempo"],
    unlocks: ["Bass", "Tempo in the sandbox"],
  },
  {
    id: "lowEnd",
    number: 5,
    title: "Low End",
    mood: "Your Home loop, with ground under its feet.",
    palette: "Your Home tape + bass",
    must: {
      id: "bassOnRoot",
      text: "Start the bass on A (1), the root.",
    },
    tries: [
      "Hold one long A under everything, then try walking A → G → E → D.",
      "Mute the bass and listen to what disappears.",
    ],
    idea: {
      title: "Bass holds it up",
      body: "The lowest note you hear shapes how everything above it feels. That's why bass lines so often play the root: it tells your ear where home is before the melody says anything. Low notes and high notes do different jobs. The bass gives weight, the bell draws the shape.",
    },
    setup: {
      bpm: 72,
      bars: 4,
      fromTape: "home",
      tracks: [
        {
          id: "pad",
          instrument: "pad",
          pitches: PAD_PENTATONIC,
          notes: heldAm(32),
        },
        {
          id: "bell",
          instrument: "bell",
          pitches: BELL_PENTATONIC,
          notes: HOME_FALLBACK_BELL,
        },
        { id: "bass", instrument: "bass", pitches: BASS_PENTATONIC },
      ],
    },
    controls: [],
    unlocks: ["Bass in the sandbox"],
  },
  {
    id: "twoMoods",
    number: 6,
    title: "Two Moods",
    mood: "A loop that drifts between wistful and warm.",
    palette:
      "Chord blocks (Am and F) · your Low End melody on the bell · bass · drums · A natural minor",
    must: {
      id: "bothChords",
      text: "Use both chords: at least one bar of Am and one of F.",
    },
    tries: [
      "Put a bass note on each chord's root: A under Am, F under F.",
      "Swap which chord comes first. Does the mood flip?",
      "Listen to your bell melody over F. Which notes sound different now?",
    ],
    idea: {
      title: "Chords set the mood",
      body: "A chord is three or more notes played together, usually stacked every other note of the scale. Am (A, C, E) is a minor chord and sounds wistful; F (F, A, C) is major and sounds warmer. F needs a note the pentatonic scale leaves out, so this tape opens the full A natural minor scale: all seven notes. In A minor, Am is chord i and F is chord VI.",
    },
    setup: {
      bpm: 72,
      bars: 8,
      scale: "naturalMinor",
      fromTape: "lowEnd",
      carry: ["bell"],
      tracks: [
        { id: "pad", instrument: "pad" },
        { id: "bell", instrument: "bell", notes: homeMelody(8) },
        { id: "bass", instrument: "bass" },
        { id: "drums", instrument: "drums", notes: softBeat(8) },
      ],
    },
    controls: ["chords"],
    chords: [0, 5],
    unlocks: [
      "Chord blocks and the full scale in the sandbox",
      "Paths (coming soon)",
    ],
  },
];

/* ---------- Deep Space ---------- */

// Preset material for the Deep Space tapes, used whenever there's no saved
// take of the tape a lesson builds on. Built with the same helpers the
// studio uses, in A natural minor.
const MINOR = { bpm: 72, scale: "naturalMinor", stepsPerBar: 8, rootMidi: 57 };

const padChords = (indexes, variant = null) => {
  let song = createSong({
    ...MINOR,
    bars: indexes.length,
    tracks: [{ id: "pad", instrument: "pad" }],
  });
  indexes.forEach((index, bar) => {
    song = setBarChord(song, "pad", bar, index, variant);
  });
  return song.tracks[0].notes;
};

// One long bass note per bar on each chord's root.
const bassRoots = (indexes) => {
  const low = INSTRUMENTS.bass.low;
  const song = createSong({ ...MINOR, bars: indexes.length });
  return indexes.map((index, bar) => {
    const chord = diatonicChord(song, index);
    return {
      pitch: low + ((chord.rootPc - (low % 12) + 12) % 12),
      step: bar * 8,
      length: 8,
    };
  });
};

const bellArp = (indexes) => {
  let song = createSong({
    ...MINOR,
    bars: indexes.length,
    tracks: [
      { id: "pad", instrument: "pad", notes: padChords(indexes) },
      { id: "bell", instrument: "bell" },
    ],
  });
  song = arpeggiate(song, "pad", "bell", "upDown", 1);
  return song.tracks[1].notes;
};

const TWO_MOODS = [0, 0, 5, 5, 0, 0, 5, 5]; // Am Am F F ...
const GRAVITY = [0, 5, 2, 6, 0, 5, 2, 6]; // Am F C G, twice
const GRAVITY_16 = [...GRAVITY, ...GRAVITY];

// `bell`: "melody" carries the Home phrase, "arp" an arpeggio of the chords.
const spaceTracks = (
  chords,
  { bell = "melody", bars = chords.length } = {}
) => [
  { id: "pad", instrument: "pad", notes: padChords(chords) },
  {
    id: "bell",
    instrument: "bell",
    notes: bell === "arp" ? bellArp(chords) : homeMelody(bars),
  },
  { id: "bass", instrument: "bass", notes: bassRoots(chords) },
  { id: "drums", instrument: "drums", notes: softBeat(bars) },
];

export const DEEP_SPACE = [
  {
    id: "nebula",
    number: 7,
    title: "Nebula",
    mood: "Your Two Moods loop, made huge.",
    palette: "Your Two Moods tape · reverb and echo for every lane",
    must: { id: "bigSpace", text: "Turn the pad's reverb past halfway." },
    tries: [
      "Turn the pad's reverb all the way up, then all the way down. Where does it start to feel like a room?",
      "Give the bass almost no reverb and the pad a lot. Which one stays clear?",
      "Add echo to the drums' snare lane and listen to it repeat on the beat.",
    ],
    idea: {
      title: "Space is an instrument",
      body: "Reverb is the sound of a room: thousands of tiny reflections that blur a note into a wash. Echo (delay) is one clear repeat, again and again, fading each time. How much of a sound goes into the room is called wet; the untouched sound is dry. Ambient music leans wet, so notes melt into each other.",
    },
    setup: {
      ...MINOR,
      bars: 8,
      fromTape: "twoMoods",
      tracks: spaceTracks(TWO_MOODS),
    },
    controls: ["space"],
    unlocks: ["Reverb and echo knobs", "The Deep Space sandbox"],
  },
  {
    id: "drift",
    number: 8,
    title: "Drift",
    mood: "A pad that swells in slowly and slowly gets brighter.",
    palette: "Your Nebula tape · brightness, swell, fade and sweep",
    must: {
      id: "slowSweep",
      text: "Give the pad a slow swell (over 1 second) and turn on its sweep.",
    },
    tries: [
      "Turn the pad's brightness all the way down, then up. Which one feels closer?",
      "Make the fade very long so each chord melts into the next.",
    ],
    idea: {
      title: "Timbre: the color of a sound",
      body: "Two sounds can play the same note and still feel different. That difference is timbre. A filter changes it by cutting away the bright, high parts of a sound: turned down it's dark and far away, turned up it's bright and close. The swell (attack) and fade (release) shape how each note begins and ends. The sweep opens the filter slowly across the loop, so the pad brightens as it plays.",
    },
    setup: {
      ...MINOR,
      bars: 8,
      fromTape: "nebula",
      tracks: spaceTracks(TWO_MOODS),
    },
    controls: ["space", "tone"],
    unlocks: ["Brightness, swell, fade and sweep knobs"],
  },
  {
    id: "gravity",
    number: 9,
    title: "Gravity",
    mood: "Four chords, each one leaning toward the next.",
    palette: "Chord blocks (Am, F, C and G) · your bell melody · bass · drums",
    must: {
      id: "fourChords",
      text: "Use all four chords: Am, F, C and G.",
    },
    tries: [
      "Try Am → F → C → G. Does G make you want to hear Am again?",
      "End the loop on F instead of G. Is the pull weaker?",
      "Put a bass note on each chord's root.",
    ],
    idea: {
      title: "Tension and release",
      body: "Chords have jobs. Am (i) is home. F (VI) and C (III) are nearby rooms, calm and warm. G (VII) leans: it sits one step below home, so after it your ear wants Am again. Moving away from home builds tension; coming back releases it. That pull is what makes a loop feel like it breathes in and out.",
    },
    setup: {
      ...MINOR,
      bars: 8,
      fromTape: "drift",
      carry: ["bell"],
      tracks: spaceTracks(TWO_MOODS),
    },
    controls: ["chords", "space", "tone"],
    chords: [0, 5, 2, 6],
    unlocks: ["C and G chord blocks"],
  },
  {
    id: "satellites",
    number: 10,
    title: "Satellites",
    mood: "Little lights circling the chords.",
    palette: "Your Gravity tape · bell · arpeggiator",
    must: {
      id: "arpeggio",
      text: "Give the bell an arpeggio: at least two bars of chord notes, one at a time.",
    },
    tries: [
      "Try each pattern: up, down, and up and down.",
      "Turn the bell's echo up. The repeats land on the beat and fill the gaps.",
      "Delete a few arpeggio notes by hand. Gaps make it breathe.",
    ],
    idea: {
      title: "Arpeggios turn chords into melody",
      body: "An arpeggio plays a chord's notes one at a time instead of all at once. Because every note comes from the chord, it always fits, and it adds motion without adding new harmony. The bell's echo is timed to the beat, so its repeats fall into the rhythm instead of blurring it.",
    },
    setup: {
      ...MINOR,
      bars: 8,
      fromTape: "gravity",
      tracks: [
        { id: "pad", instrument: "pad", notes: padChords(GRAVITY) },
        { id: "bell", instrument: "bell", notes: homeMelody(8) },
        { id: "bass", instrument: "bass", notes: bassRoots(GRAVITY) },
        { id: "drums", instrument: "drums", notes: softBeat(8) },
      ],
    },
    controls: ["chords", "arp", "space", "tone"],
    chords: [0, 5, 2, 6],
    unlocks: ["The arpeggiator"],
  },
  {
    id: "weightless",
    number: 11,
    title: "Weightless",
    mood: "Chords that float and never quite land.",
    palette: "Your Satellites tape · sus chords",
    must: {
      id: "susChord",
      text: "Turn at least one chord into a sus chord.",
    },
    tries: [
      "Make every chord sus2. Does the loop still feel sad or happy?",
      "Keep Am plain and make only G sus4. Hear it hang before coming home?",
      "Re-run the arpeggiator after changing chords so the bell follows.",
    ],
    idea: {
      title: "Why ambient music floats",
      body: "A chord's middle note, the third, decides if it sounds major (bright) or minor (wistful). A sus chord swaps the third for the note just below it (sus2) or just above it (sus4). With no third, the chord is neither happy nor sad; it just hangs in the air. That open, unresolved sound is a big part of why ambient music feels weightless.",
    },
    setup: {
      ...MINOR,
      bars: 8,
      fromTape: "satellites",
      tracks: spaceTracks(GRAVITY, { bell: "arp" }),
    },
    controls: ["chords", "sus", "arp", "space", "tone"],
    chords: [0, 5, 2, 6],
    unlocks: ["Sus chords"],
  },
  {
    id: "lightYears",
    number: 12,
    title: "Light Years",
    mood: "A journey: a quiet start, a build, a drift, and a return.",
    palette: "Your Weightless tape, twice as long · sections",
    must: {
      id: "textureArc",
      text: "Switch some lanes off in some sections, so the sections don't all sound the same.",
    },
    tries: [
      "Start with just the pad in the intro.",
      "Take the drums out of the drift section.",
      "Bring everything back for the return.",
    ],
    idea: {
      title: "Form: a track is a journey",
      body: "Form is how a track changes over time. The simplest tool is adding and removing layers: start thin, build up, strip back, then bring it all home. The notes can stay the same; what changes is which parts are playing. Your ear hears each change as a new chapter.",
    },
    setup: {
      ...MINOR,
      bars: 16,
      fromTape: "weightless",
      sections: ["Intro", "Build", "Drift", "Return"],
      tracks: spaceTracks(GRAVITY_16, { bell: "arp" }),
    },
    controls: ["sections", "chords", "sus", "arp", "space", "tone"],
    chords: [0, 5, 2, 6],
    unlocks: ["Sections"],
  },
  {
    id: "transmission",
    number: 13,
    title: "Transmission",
    mood: "Anything you like. Everything is unlocked.",
    palette: "Your Light Years tape · every tool",
    must: {
      id: "transmission",
      text: "Fill at least three lanes, then save it.",
    },
    tries: [
      "Change the tempo and see what mood it becomes.",
      "Try a chord you haven't used yet, like Dm or Em.",
      "Play it for someone.",
    ],
    idea: {
      title: "You know how this works now",
      body: "Every track you've heard is built from the same few choices: sounds, notes, rhythm, chords, space and form. You've made each of them yourself. This tape has no lesson, only an open brief. Listen to music you love and try to hear which choices it made.",
    },
    setup: {
      ...MINOR,
      bars: 16,
      fromTape: "lightYears",
      sections: ["Intro", "Build", "Drift", "Return"],
      tracks: spaceTracks(GRAVITY_16, { bell: "arp" }),
    },
    controls: ["tempo", "sections", "chords", "sus", "arp", "space", "tone"],
    chords: [0, 2, 3, 4, 5, 6],
    unlocks: ["Dm and Em chord blocks in the Deep Space sandbox"],
  },
];

/* ---------- Paths ---------- */

// Side A comes first for everyone; paths open after its last tape.
export const PATHS = [
  {
    id: "deepSpace",
    name: "Deep Space",
    blurb:
      "Spacey, ambient instrumentals: reverb, slow swells, arpeggios and chords that float.",
    theme: "deepSpace",
    tapes: DEEP_SPACE,
  },
];

// Every tape, in play order, each marked with its list (`side`) and the
// tape that has to be finished first (`requires`, null for the very first).
const withOrder = (tapes, side, firstRequires) =>
  tapes.map((t, i) => ({
    ...t,
    side,
    requires: i === 0 ? firstRequires : tapes[i - 1].id,
  }));

export const ALL_TAPES = [
  ...withOrder(SIDE_A, "sideA", null),
  ...PATHS.flatMap((p) =>
    withOrder(p.tapes, p.id, SIDE_A[SIDE_A.length - 1].id)
  ),
];

// Tapes announced on the hub but not playable yet.
export const COMING_SOON = [];

export const getTape = (id) => ALL_TAPES.find((t) => t.id === id) || null;

export const getPath = (id) => PATHS.find((p) => p.id === id) || null;

// The next tape in the same list (Side A or a path), or null at its end.
export const nextTape = (id) => {
  const tape = getTape(id);
  if (!tape) return null;
  const list = ALL_TAPES.filter((t) => t.side === tape.side);
  return list[list.findIndex((t) => t.id === id) + 1] || null;
};

// Notes of a shorter song repeated to fill `bars` bars.
const tileNotes = (notes, fromBars, bars, stepsPerBar) => {
  const out = [];
  for (let offset = 0; offset < bars; offset += fromBars) {
    notes.forEach((n) => {
      const step = n.step + offset * stepsPerBar;
      if (step < bars * stepsPerBar) out.push({ ...n, step });
    });
  }
  return out;
};

// A fresh song for a tape, shaped by its setup. When the tape builds on an
// earlier one (`setup.fromTape`) and a saved song from that tape is given,
// its notes and sound settings replace the setup's for every track the two
// share, or only the tracks listed in `setup.carry` (repeated to fill the
// tape if the earlier song was shorter).
export const tapeSong = (tape, fromSong = null) => {
  let song = createSong(tape.setup);
  if (tape.setup.sections) song = makeSections(song, tape.setup.sections);
  if (!fromSong || !tape.setup.fromTape) return song;
  return {
    ...song,
    tracks: song.tracks.map((t) => {
      if (tape.setup.carry && !tape.setup.carry.includes(t.id)) return t;
      const src = fromSong.tracks.find((f) => f.id === t.id);
      if (!src) return t;
      const notes =
        fromSong.bars < song.bars
          ? tileNotes(src.notes, fromSong.bars, song.bars, song.stepsPerBar)
          : src.notes
              .filter((n) => n.step < song.bars * song.stepsPerBar)
              .map((n) => ({ ...n }));
      return src.sound
        ? { ...t, notes, sound: { ...src.sound } }
        : { ...t, notes };
    }),
  };
};

// Loop length for the brief, rounded to whole seconds ("7 sec").
export const tapeLengthLabel = (tape) =>
  `${Math.round(loopSeconds(tapeSong(tape)))} sec`;
